import { apiFetch, apiFetchBytes } from './client.js';

// #774 — screenshots and PDFs attached to a case's evidence
// (apps/api/src/routes/evidence-files.ts).
//
// Under `img-src 'self' data:` with the bearer token held in JS memory, an
// image the API serves cannot be an `<img src="/api/...">` — that request
// carries no token and 401s — and a `blob:` URL is outside img-src. So an
// image is read with the token (`apiFetchBytes`, as #767's logo snapshots are)
// and rendered as a `data:` URL. A PDF is never rendered here; it downloads.

export type EvidenceSection = 'kyc' | 'shaft' | 'ip' | 'trust';

/** Where a file is attached: a KYC evidence record, or a review section. */
export type EvidenceTarget =
  | { section: 'kyc'; kycItem: string; evidenceId: string }
  | { section: 'shaft' | 'ip' | 'trust' };

export interface EvidenceFile {
  id: number;
  section: EvidenceSection;
  kycItem: string | null;
  evidenceId: string | null;
  filename: string;
  contentType: string;
  byteLength: number;
  sha256: string;
  uploadedAt: string;
  /** `users.id`, not the Keycloak sub. */
  uploadedBy: string;
  uploadedByName: string | null;
}

/** The per-file limit the API enforces (MAX_EVIDENCE_FILE_BYTES, the upload
 *  route's bodyLimit). Checked here first only so an analyst is told before a
 *  50 MiB upload, not after it. A COPY, tied by disk-read pins in both
 *  packages (#589, docs/CROSS_PACKAGE_CONSTANTS.md) — evidenceFiles.test.ts here. */
export const MAX_EVIDENCE_FILE_BYTES = 50 * 1024 * 1024;

/**
 * The only types an evidence image is ever rendered as. A COPY of the API's
 * `EVIDENCE_IMAGE_TYPES` (apps/api/src/lib/file-magic.ts, which sniffs the
 * bytes), tied by disk-read pins in both packages (#589,
 * docs/CROSS_PACKAGE_CONSTANTS.md) — EvidenceAttachments.test.tsx here.
 */
export const EVIDENCE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const EVIDENCE_PDF_TYPE = 'application/pdf';

/** What the file picker offers. The server decides by the bytes regardless. */
export const EVIDENCE_ACCEPT = [...EVIDENCE_IMAGE_TYPES, EVIDENCE_PDF_TYPE].join(',');

export function isEvidenceImage(contentType: string): boolean {
  return (EVIDENCE_IMAGE_TYPES as readonly string[]).includes(contentType);
}

/** Whether `file` belongs to `target`. */
export function attachedTo(file: EvidenceFile, target: EvidenceTarget): boolean {
  if (file.section !== target.section) return false;
  if (target.section !== 'kyc') return true;
  return file.kycItem === target.kycItem && file.evidenceId === target.evidenceId;
}

const base = (caseId: string) => `/cases/${encodeURIComponent(caseId)}/evidence-files`;

/** The case's files, not removed, oldest first. */
export async function listEvidenceFiles(caseId: string): Promise<EvidenceFile[]> {
  const res = await apiFetch<{ caseId: string; items: EvidenceFile[] }>(base(caseId));
  return res.items;
}

/** Upload one file as its raw bytes. The API scans it before storing it. */
export function uploadEvidenceFile(caseId: string, target: EvidenceTarget, file: Blob, filename: string): Promise<EvidenceFile> {
  const q = new URLSearchParams({ section: target.section, filename });
  if (target.section === 'kyc') {
    q.set('kycItem', target.kycItem);
    q.set('evidenceId', target.evidenceId);
  }
  return apiFetch<EvidenceFile>(`${base(caseId)}?${q.toString()}`, {
    method: 'POST',
    body: file,
    headers: { 'Content-Type': 'application/octet-stream' },
  });
}

/** Soft-remove: hidden from the case, kept for retention, audited with the reason. */
export function removeEvidenceFile(caseId: string, fileId: number, reason: string): Promise<unknown> {
  return apiFetch(`${base(caseId)}/${fileId}`, { method: 'DELETE', body: JSON.stringify({ reason }) });
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('could not read the image'));
    reader.readAsDataURL(blob);
  });
}

/**
 * One image as a `data:` URL for an `<img src>`. Refuses any type outside the
 * three image types rather than building a `data:` URL of whatever came back.
 */
export async function evidenceImageDataUrl(caseId: string, fileId: number): Promise<string> {
  const { bytes, contentType } = await apiFetchBytes(`${base(caseId)}/${fileId}/content`);
  const type = contentType.split(';')[0].trim().toLowerCase();
  if (!isEvidenceImage(type)) throw new Error(`evidence file ${fileId} is not an image (${type || 'no type'})`);
  return readAsDataUrl(new Blob([bytes], { type }));
}

/**
 * Download one file (a PDF, or an image saved rather than viewed). The bytes
 * are read with the token and handed to the browser's download through an
 * object URL on an `<a download>`. That URL is never loaded as an image or a
 * page — a download is not governed by `img-src` or any other fetch directive
 * of the CSP — and it is revoked shortly after the click (immediately can
 * cancel the download in some browsers). A `data:` URL is not used here
 * because browsers cap URL length well below 50 MiB.
 */
export async function downloadEvidenceFile(caseId: string, file: Pick<EvidenceFile, 'id' | 'filename'>): Promise<void> {
  const { bytes, contentType } = await apiFetchBytes(`${base(caseId)}/${file.id}/content`);
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType.split(';')[0].trim() }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
